import fetchTrendingContent from 'services/api/bannerList/fetchTrendingContent';
import sdnApi from 'services/api/sdn';
import localeUtils from 'utils/LanguageLocale';
import { getOrCreateAbTestUuid } from 'utils/localStorage/AbTestUuid';

// CMS blPlacement -> trending service placement code.
/* eslint-disable camelcase */
const TRENDING_CONTENT = {
    homepage_marketingbanner: 'hpmktg',
    homepage_herocarousel: 'hphc'
};
/* eslint-enable camelcase */

/**
 * Fetches trending content for a single banner-list placement and returns the normalized
 * `{ variationData, akamaiabtestdata }` shape consumed by BannerList.
 *
 * Encapsulates the dual fetch path (AB-test service vs legacy trending service), the AB test
 * UUID logic, the akamai-header parsing and the error fallback — everything that previously
 * lived across withBannerListProps.getTrendingContent and BannerList.componentDidMount.
 *
 * Never rejects — on failure it resolves to the default fallback.
 */
async function getTrendingContentData({ blPlacement, personalization = {} }) {
    const placement = TRENDING_CONTENT[blPlacement];
    const { isTrendingEnabled = false, trendingContentAB = {} } = personalization || {};
    const { isABTestEnabled = false, abTestId, abTestEndDate } = trendingContentAB;

    // Generate or retrieve AB test-scoped UUID when isTrendingEnabled is true and abTestId is present
    let uuid = null;

    if (isTrendingEnabled && abTestId) {
        uuid = getOrCreateAbTestUuid(abTestId, abTestEndDate);

        if (!uuid) {
            Sephora.logger.error('Personalization: Failed to generate AB test UUID');
        }
    }

    try {
        // If both isTrendingEnabled and trendingContentAB.isABTestEnabled are true, use AB test service.
        // Otherwise, use the existing trending content service.
        const response =
            isTrendingEnabled && isABTestEnabled
                ? await sdnApi.getAbTestPersonalization({
                    deviceId: uuid,
                    channel: 'web',
                    placement,
                    locale: localeUtils.getCurrentLanguageLocale()
                })
                : await fetchTrendingContent(placement, uuid);

        const { headers, data: trendingContent, metadata } = response || {};

        // Extract akamai headers, for tracking purposes. The AB-test service does not return
        // headers, so these resolve to `undefined` on that path (preserved intentionally).
        const akamaiid = headers?.get('akamaiABTestID');
        const akamaistartdate = headers?.get('akamaiAbTestStartDate');
        const akamaienddate = headers?.get('akamaiAbTestEndDate');
        const akamaiabtestdata = `akamaiABTestID:${akamaiid};akamaiAbTestStartDate:${akamaistartdate};akamaiAbTestEndDate:${akamaienddate};`;
        const trendingContentData = trendingContent?.at(0);

        if (trendingContentData?.variationData) {
            return {
                variationData: trendingContentData.variationData,
                akamaiabtestdata: akamaiabtestdata + 'isDefault:false'
            };
        }

        return {
            variationData: null,
            akamaiabtestdata: akamaiabtestdata + `isDefault:true;abGroup:${metadata?.abGroup || ''}`
        };
    } catch (error) {
        Sephora.logger.error('Personalization: Failed to load trending content', error);

        return {
            variationData: null,
            akamaiabtestdata: 'isDefault:true'
        };
    }
}

export { TRENDING_CONTENT };

export default getTrendingContentData;
